import { FormCheckbox } from '@shared/components';
import { useFormCheckbox } from '@shared/hooks';
import { useFormContext } from 'react-hook-form';

import { BULK_PERMISSION_NAME } from '../../constants';
import type {
  AppPermissionDetails,
  ActionPermissionType,
  AppRolePermissionForm,
} from '../../types';
import {
  autoCheckAction,
  autoUncheckAction,
  extractToCategoryAndName,
  updateCheckboxIfDefined,
} from '../../utils';

interface BulkPermissionCheckboxProps {
  category: string;
  name: string;
  action: ActionPermissionType;
  permissions: AppPermissionDetails[];
}

export default function BulkPermissionCheckbox(props: Readonly<BulkPermissionCheckboxProps>) {
  const { category, name, action, permissions } = props;
  const formContext = useFormContext<AppRolePermissionForm>();
  const groupValues = formContext.getValues(`${category}`) ?? {};

  const keys = Object.keys(groupValues).filter((key) => key !== BULK_PERMISSION_NAME);

  const {
    updateCheckboxState,
    selectAll: {
      state: { indeterminate: shouldIndeterminate, checked: isChecked },
    },
  } = useFormCheckbox({
    data: [
      {
        name: `${category}.${name}.${action}`,
        children: keys
          .filter((key) => formContext.getValues(`${category}.${key}.${action}`) !== undefined)
          .map((key) => ({
            name: `${category}.${key}.${action}`,
          })),
      },
    ],
  });

  const handleBulkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const manipulatedActions = isChecked ? autoCheckAction(action) : autoUncheckAction(action);

    for (const nextAction of manipulatedActions) {
      updateCheckboxIfDefined(formContext, category, name, nextAction, isChecked);
    }

    updateCheckboxState(`${category}.${name}.${action}`, isChecked);

    for (const permission of permissions) {
      if (permission.type === 'feature' && permission.header === category) {
        const { category: permissionCategory, name: permissionName } = extractToCategoryAndName(
          permission.id,
        );
        updateCheckboxIfDefined(formContext, permissionCategory, permissionName, action, isChecked);
        for (const nextAction of manipulatedActions) {
          if (
            formContext.getValues(`${permissionCategory}.${permissionName}.${action}`) !== undefined
          ) {
            updateCheckboxIfDefined(
              formContext,
              permissionCategory,
              permissionName,
              nextAction,
              isChecked,
            );
            updateCheckboxIfDefined(
              formContext,
              permissionCategory,
              BULK_PERMISSION_NAME,
              nextAction,
              isChecked,
            );
          }
        }
      }
    }
  };

  return (
    <FormCheckbox
      name={`${category}.${name}.${action}`}
      onChange={handleBulkChange}
      sx={(theme) => ({
        fontSize: '24px',
        color: theme.palette.grey[400],
        p: theme.spacing(0),
      })}
      indeterminate={shouldIndeterminate}
      checked={isChecked}
      // NOTE: Force the checkbox to re-render to update its state to indeterminate
      disabled={false}
    />
  );
}
